import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { GET, POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import AuthContext from "../../store/auth-context";
import TenantCard from "../../components/Cards/TenantCard/TenantCard";
import CreateTenantModal from "../../common/CreateTenantModal/CreateTenantModal";
import CertificateTypeChooserModal, {
  CERTIFICATE_DESIGN_TYPE,
  CERTIFICATE_ONBOARDING_ROUTES,
} from "../../common/CertificateOnboarding/components/CertificateTypeChooserModal";
import { isHtmlDesignsBuildEnabled } from "../../hooks/useHtmlDesignsEnabled";
import SimpleModal from "../../common/SimpleModal/SimpleModal";
import warningIcon from "../../resources/icons/warning-info.svg";
import EntityConstants from "../../configs/EntityConstants";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const Tenant = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const fetchSeqRef = useRef(0);
  const isFetchingRef = useRef(false);
  const { t } = useTranslation();
  const isSuperAdmin = authContext.userType?.toLowerCase() === "super_admin";

  const [limit] = useState(6);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCertificateTypeChooser, setShowCertificateTypeChooser] =
    useState(false);
  const [updatingTenantCode, setUpdatingTenantCode] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    tenant: null,
  });
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isToggleRefresh, setIsToggleRefresh] = useState(false);
  const [hasActiveTenant, setHasActiveTenant] = useState(false);
  const [activeTenantLoading, setActiveTenantLoading] = useState(true);
  const lastFetchParamsRef = useRef({ offset: null, search: null });
  const isCreateCertificateDisabled = activeTenantLoading || !hasActiveTenant;

  const fetchTenants = useCallback(
    async (offset = 0, search = "", force = false, isToggle = false) => {
      const seq = ++fetchSeqRef.current;

      if (
        !force &&
        isFetchingRef.current &&
        lastFetchParamsRef.current.offset === offset &&
        lastFetchParamsRef.current.search === search
      ) {
        return;
      }

      isFetchingRef.current = true;
      lastFetchParamsRef.current = { offset, search };

      if (!isToggle) {
        setIsSearching(true);
      }

      try {
        const endpoint = search
          ? `${
              EntityConstants.GET_TENANT_DATA
            }?limit=${limit}&offset=${offset}&search=${encodeURIComponent(
              search,
            )}`
          : `${EntityConstants.GET_TENANT_DATA}?limit=${limit}&offset=${offset}`;

        const response = await GET(endpoint);
        const data = await response.json();
        const resData = data.data;

        // Only apply the latest in-flight response
        if (seq !== fetchSeqRef.current) return;

        if (resData?.error) {
          Toast({
            type: "error",
            title: t("tenant:error"),
            message: resData.error,
          });
          setTenants([]);
          setPrevious(null);
          setNext(null);
        } else {
          setTenants(resData?.results || []);
          setPrevious(resData?.previous);
          setNext(resData?.next);
          authContext.TenantOffsetHandler(offset);
        }
      } catch (error) {
        console.error("Failed to load tenants", error);

        if (seq === fetchSeqRef.current) {
          Toast({
            type: "error",
            title: t("tenant:error"),
            message: t("tenant:failed_load_tenants"),
          });
          setTenants([]);
          setPrevious(null);
          setNext(null);
        }
      } finally {
        if (seq === fetchSeqRef.current) {
          setIsSearching(false);
          setLoading(false);
          setIsToggleRefresh(false);
          isFetchingRef.current = false;
        }
      }
    },
    [authContext, limit, t],
  );

  const fetchHasActiveTenant = useCallback(async () => {
    setActiveTenantLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "1",
        offset: "0",
        is_active: "true",
      });
      const response = await GET(
        `${EntityConstants.GET_TENANT_DATA}?${params.toString()}`,
      );
      const data = await response.json();
      const results = data.data?.results || [];
      setHasActiveTenant(results.length > 0);
    } catch (error) {
      console.error("Failed to check active tenants", error);
      setHasActiveTenant(false);
    } finally {
      setActiveTenantLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants(authContext.tenantOffset || 0);
    if (isSuperAdmin) {
      fetchHasActiveTenant();
    }
  }, []);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.trim()) {
        fetchTenants(0, searchValue, true);
      } else {
        fetchTenants(0, "", true);
      }
    }, 500),
    [fetchTenants],
  );

  const handleToggleActive = async (tenant) => {
    setConfirmModal({ show: true, tenant });
  };

  const confirmToggle = async () => {
    const tenant = confirmModal.tenant;
    setUpdatingTenantCode(tenant.code);
    setConfirmModal({ show: false, tenant: null });

    try {
      const response = await POST(EntityConstants.POST_UPDATE_TENANT_STATUS, {
        tenant_code: tenant.code,
        status: !tenant.is_active,
      });

      if (response.ok) {
        Toast({
          type: "success",
          title: t("tenant:success"),
          message: `${t("tenant:tenant")} ${
            !tenant.is_active ? t("tenant:activated") : t("tenant:deactivated")
          } ${t("tenant:successfully")}`,
        });
        await fetchTenants(
          authContext.tenantOffset || 0,
          searchTerm,
          true,
          true,
        );
        if (isSuperAdmin) {
          await fetchHasActiveTenant();
        }
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: errorData.error || t("tenant:failed_update_tenant_status"),
        });
      }
    } catch (error) {
      console.error("Error updating tenant status:", error);
      Toast({
        type: "error",
        title: t("tenant:error"),
        message: t("tenant:failed_update_tenant_status"),
      });
    }
    setUpdatingTenantCode(null);
  };

  const cancelToggle = () => {
    setConfirmModal({ show: false, tenant: null });
  };

  const handlePrevious = async () => {
    if (previous !== null) {
      const prevOffset = Math.max(0, authContext.tenantOffset - limit);
      await fetchTenants(prevOffset, searchTerm, true);
    }
  };

  const handleNext = async () => {
    if (next !== null) {
      const nextOffset = authContext.tenantOffset + limit;
      await fetchTenants(nextOffset, searchTerm, true);
    }
  };

  const handleTenantCreated = () => {
    fetchTenants(authContext.tenantOffset || 0, searchTerm, true);
    if (isSuperAdmin) {
      fetchHasActiveTenant();
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearching(true);
    debouncedSearch(value);
  };

  return (
    <div
      className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.tenant}
    >
      <div className="w-full bg-cfSearchContainer flex justify-start py-[0.8rem] px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start md:items-center gap-4 w-full">
          <p className="text-cfHeadingText font-semibold">
            {t("tenant:tenant_management")}
          </p>

          <div className="flex flex-col md:flex-row items-start gap-3">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() =>
                  isHtmlDesignsBuildEnabled()
                    ? setShowCertificateTypeChooser(true)
                    : navigate(
                        CERTIFICATE_ONBOARDING_ROUTES[
                          CERTIFICATE_DESIGN_TYPE.SVG
                        ],
                      )
                }
                id="tenant-create-certificate"
                disabled={isCreateCertificateDisabled}
                className="px-4 py-2 bg-cfPrimaryButton text-white font-medium rounded-md transition-colors duration-200 hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-cfPrimaryButton"
              >
                {t("tenant:create_certificate")}
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              id="tenant-create-tenant"
              className="px-4 py-2 bg-cfPrimaryButton text-white font-medium rounded-md hover:bg-cfPrimaryDark transition-colors duration-200"
            >
              {t("tenant:create_tenant")}
            </button>

            <input
              type="text"
              name="search"
              id="tenant-input-search"
              placeholder={t("tenant:search_tenants_name_code")}
              className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
              style={{
                width: "350px",
                fontSize: "0.875rem",
                lineHeight: "1.4rem",
              }}
              value={searchTerm}
              onChange={handleSearchInputChange}
            />
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col mt-2">
        <div className="overflow-x-auto">
          <div className="flex flex-col justify-center gap-2 min-w-[90.5rem] md:w-full px-4">
            <div className="flex bg-cfTableHeaderBackgroundColor items-center min-h-[4.5rem] border text-center min-w-[78.8rem] px-6 font-bold text-[14px]">
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                {t("tenant:name")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                {t("tenant:code")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                {t("tenant:type")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                {t("tenant:status")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                {t("tenant:action")}
              </div>
            </div>

            {isSearching && !isToggleRefresh ? (
              <div className="flex justify-center items-center min-h-[8rem] mt-16">
                <CfLoader />
              </div>
            ) : loading ? (
              <div className="flex justify-center items-center min-h-[8rem] mt-16">
                <CfLoader />
              </div>
            ) : tenants.length === 0 ? (
              <div
                id="tenant-empty-message"
                className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg"
              >
                {t("tenant:no_tenants_found")}
              </div>
            ) : (
              tenants.map((tenant) => (
                <TenantCard
                  key={tenant.code}
                  tenant={tenant}
                  onToggleActive={handleToggleActive}
                  updatingTenantCode={updatingTenantCode}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 px-8 w-full">
        <button
          onClick={handlePrevious}
          id="tenant-pagination-previous"
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={previous === null || isSearching}
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>
            <span>{t("tenant:previous")}</span>
          </span>
        </button>

        <button
          onClick={handleNext}
          id="tenant-pagination-next"
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={next === null || isSearching}
        >
          <span className="flex items-center justify-center gap-2">
            <span>{t("tenant:next")}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>

      <CreateTenantModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTenantCreated={handleTenantCreated}
      />

      {isSuperAdmin && (
        <CertificateTypeChooserModal
          isOpen={showCertificateTypeChooser}
          onClose={() => setShowCertificateTypeChooser(false)}
          onSelect={(type) => {
            setShowCertificateTypeChooser(false);
            navigate(CERTIFICATE_ONBOARDING_ROUTES[type]);
          }}
        />
      )}

      <SimpleModal
        isOpen={confirmModal.show}
        closeModal={cancelToggle}
        successFunction={confirmToggle}
        modalIcon={warningIcon}
        modalHeaderMessage={`${
          confirmModal.tenant?.is_active
            ? t("tenant:deactivate")
            : t("tenant:activate")
        } Tenant`}
        modalBodyMessage={`${t("tenant:are_you_sure_want_to")} ${
          confirmModal.tenant?.is_active
            ? t("tenant:deactivate_text")
            : t("tenant:activate_text")
        } "${confirmModal.tenant?.name}"?`}
        leftButtonMessage={t("tenant:cancel")}
        rightButtonMessage={t("tenant:confirm")}
        testId="tenant-confirm-toggle-modal"
      />
    </div>
  );
};

export default Tenant;
